import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from './ui/button';
import { ResponsiveLogo } from './ThemeLogo';
import { Moon, Sun, Menu, X } from 'lucide-react';

const NAV_LINKS = [
  { label: 'About Us', href: '/about-us' },
  { label: 'MrCanvas', href: '/strategy-coach' },
  { label: 'Templates', href: '/templates' },
  { label: 'Blogs', href: '/blog' },
  { label: 'Contact', href: '/contact' },
];

export default function PublicHeader() {
  const { isAuthenticated } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <nav className="sticky top-0 z-50 bg-background/90 backdrop-blur-xl border-b border-border" data-testid="public-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16">
        {/* Left — Logo */}
        <Link to="/" className="shrink-0">
          <ResponsiveLogo className="w-auto" style={{ height: '2.5rem' }} />
        </Link>

        {/* Center — Nav Links (desktop) */}
        <div className="hidden lg:flex items-center gap-6">
          {NAV_LINKS.map(link => {
            const active = location.pathname === link.href || (link.href !== '#' && location.pathname.startsWith(link.href + '/'));
            return link.href === '#' ? (
              <span key={link.label} className="text-sm font-medium text-muted-foreground cursor-default">
                {link.label}
              </span>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                className={`text-sm font-medium transition-colors ${active ? 'text-odapto-orange' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {link.label}
              </Link>
            );
          })}
        </div>

        {/* Right — Actions */}
        <div className="flex items-center gap-2">
          <button
            onClick={toggleTheme}
            className="p-2 rounded-full hover:bg-muted transition-colors"
            data-testid="theme-toggle-public"
          >
            {theme === 'dark' ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
          </button>

          {isAuthenticated ? (
            <Link to="/dashboard">
              <Button className="bg-odapto-orange hover:bg-odapto-orange-hover text-white text-sm rounded-full px-5">
                Dashboard
              </Button>
            </Link>
          ) : (
            <>
              <Link to="/login" className="text-sm font-medium text-muted-foreground hover:text-foreground hidden sm:block">
                Login
              </Link>
              <Link to="/register">
                <Button className="bg-odapto-orange hover:bg-odapto-orange-hover text-white text-sm rounded-full px-5" data-testid="start-free-btn">
                  Start Free
                </Button>
              </Link>
            </>
          )}

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 rounded-lg hover:bg-muted transition-colors ml-1"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="lg:hidden border-t border-border bg-background px-4 py-4 space-y-1" data-testid="mobile-menu">
          {NAV_LINKS.map(link => {
            const active = location.pathname === link.href || (link.href !== '#' && location.pathname.startsWith(link.href + '/'));
            return link.href === '#' ? (
              <span key={link.label} className="block py-2 text-sm text-muted-foreground">
                {link.label}
              </span>
            ) : (
              <Link
                key={link.label}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className={`block py-2 text-sm font-medium transition-colors ${active ? 'text-odapto-orange' : 'text-muted-foreground hover:text-foreground'}`}
              >
                {link.label}
              </Link>
            );
          })}
        </div>
      )}
    </nav>
  );
}
